import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatErr } from "../lib/api";
import LanguageSelector from "../components/LanguageSelector";

const LOGO = "https://customer-assets.emergentagent.com/job_project-sync-pro-1/artifacts/y56otaul_Virtually%20%281%29.png";

export default function Login() {
    const { t } = useTranslation();
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError(""); setLoading(true);
        try {
            const u = await login(email, password);
            nav(u.role ? "/dashboard" : "/choose-role");
        } catch (err) {
            setError(formatErr(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    };

    const google = () => {
        // OAuth callback must land on the SAME origin the user is browsing —
        // if we derived it from REACT_APP_BACKEND_URL instead, a user viewing
        // the app from a preview host / mirror / custom-domain would trigger
        // a cross-origin "Network Error" when the callback fires.
        const redirect = window.location.origin + "/auth/callback";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    return (
        <div className="min-h-screen grain grid md:grid-cols-2" data-testid="login-page">
            <div className="hidden md:block relative">
                <img src="https://images.unsplash.com/photo-1584892905725-bdac5b99e55a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                     alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-br from-[var(--brand-purple)]/60 via-[var(--brand-magenta)]/30 to-[var(--brand-orange)]/40 mix-blend-multiply" />
                <div className="relative z-[2] h-full flex flex-col justify-end p-10 text-white">
                    <div className="font-script text-3xl">welcome back</div>
                    <div className="font-serif text-4xl leading-tight">Rise. Resume. <em>Repeat.</em></div>
                </div>
            </div>
            <div className="flex items-center justify-center p-6 md:p-12 relative z-[2]">
                <form onSubmit={submit} className="w-full max-w-sm card-warm p-8 fade-up" data-testid="login-form">
                    <div className="flex items-start justify-between gap-3 mb-6">
                        <div className="flex items-center gap-3">
                            <img src={LOGO} alt="" className="h-10 w-10 object-contain" />
                            <div>
                                <div className="font-serif text-lg">Virtually Unstoppable</div>
                                <div className="text-xs text-[var(--text-ink)]/60">{t("auth.loginSubtitle")}</div>
                            </div>
                        </div>
                        <LanguageSelector compact testid="login-language-selector" />
                    </div>
                    <button type="button" onClick={google} data-testid="login-google-btn"
                            className="w-full btn-ghost flex items-center justify-center gap-2 mb-3">
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-4" />
                        {t("auth.withGoogle")}
                    </button>
                    <div className="my-4 flex items-center gap-3 text-xs text-[var(--text-ink)]/50">
                        <div className="flex-1 h-px bg-[rgba(26,19,24,0.12)]" /> {t("auth.or")} <div className="flex-1 h-px bg-[rgba(26,19,24,0.12)]" />
                    </div>
                    <label className="text-sm">{t("auth.email")}</label>
                    <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required
                           data-testid="login-email" className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] focus:outline-none focus:border-[var(--brand-purple)]" />
                    <label className="text-sm mt-3 block">{t("auth.password")}</label>
                    <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required
                           data-testid="login-password" className="w-full mt-1 px-3 py-2 rounded-xl border border-[rgba(26,19,24,0.15)] focus:outline-none focus:border-[var(--brand-purple)]" />
                    {error && <div className="mt-3 text-sm text-[var(--status-red)]" data-testid="login-error">{error}</div>}
                    <button disabled={loading} type="submit" data-testid="login-submit-btn"
                            className="btn-primary w-full mt-5">{loading ? t("auth.signingIn") : t("auth.signIn")}</button>
                    <div className="flex justify-between mt-4 text-sm">
                        <Link to="/forgot" data-testid="login-forgot-link" className="text-[var(--brand-purple)] hover:underline">{t("auth.forgot")}</Link>
                        <Link to="/signup" data-testid="login-signup-link" className="text-[var(--brand-orange)] hover:underline">{t("auth.signUp")}</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
